/**
 * What a leader is allowed to read of what people wrote about them.
 *
 * Prose is not a number. A mean of 2.4 from six peers tells you nothing about
 * who said what; a sentence naming a meeting last March can identify its author
 * to a room of three. So the rules here are deliberately stricter than the ones
 * governing scores in confidentiality.ts:
 *
 *  1. Comments from the anonymous relationships are **pooled**, not grouped.
 *     The scores are broken down by segment once a segment reaches the minimum;
 *     the comments never are. Knowing "a peer wrote this" narrows the field from
 *     twenty people to six, and with a specific example that is often enough.
 *  2. The pool is shown only once it holds at least MIN_SEGMENT_SIZE comments.
 *     Two comments in a pool of two is not a pool.
 *  3. The order is a stable shuffle, never the order they arrived. Submission
 *     order lines up with the reminder emails and with anything the leader
 *     knows about who answers early, and would slowly leak authorship.
 *  4. The supervisor's comment is shown, attributed. This follows the client's
 *     ruling on the supervisor's scores — a leader should know what their
 *     supervisor said, and a single supervisor cannot be anonymised anyway.
 *     It is the one deliberate exception, and it is labelled as such so nobody
 *     mistakes it for part of the anonymous pool.
 *
 * A rater writing knows all of this before they type: the survey says whether
 * their words will be attributed or pooled.
 */
import { MIN_SEGMENT_SIZE } from "./confidentiality.js";
/**
 * How much one person may write.
 *
 * The prompt invites them to write at whatever length they want, so this is a
 * storage bound rather than an editorial one — high enough that nobody with
 * something to say meets it. Mirrored by a CHECK constraint on the table.
 */
export const OPEN_COMMENT_MAX_LENGTH = 4000;
/** Below this many pooled comments, none of them are shown at all. */
export const MIN_POOLED_COMMENTS = MIN_SEGMENT_SIZE;
/**
 * The shortest answer that counts as an answer.
 *
 * The open question is required, and "required" without a floor means a full
 * stop gets somebody to the send button — which is worse than a bad paragraph,
 * because at least a bad paragraph can be read.
 *
 * Four words, counted rather than four dozen characters: a character minimum
 * is defeated by holding down a key, and while a word minimum can be gamed
 * too, it costs more and it shows when a human reads it. Four is low on
 * purpose. Somebody who genuinely sees little of a colleague should be able to
 * say so — "lo veo poco, poca opinión" is four words and is honest — while
 * "ok" and "." are not answers.
 */
export const MIN_COMMENT_WORDS = 4;
/**
 * Words, where a word has to contain a letter or a digit.
 *
 * Otherwise ". . . ." is four words, which is exactly the shape of somebody
 * getting past a minimum rather than meeting it.
 */
export const countWords = (text) => text
    .trim()
    .split(/\s+/)
    .filter((token) => /[\p{L}\p{N}]/u.test(token)).length;
/** Whether an open answer is long enough to be submitted. */
export const isCommentSubstantial = (text) => countWords(text) >= MIN_COMMENT_WORDS;
/**
 * A stable order that is not submission order.
 *
 * Sorting on the identifier does it: these are random UUIDs, so the sequence is
 * arbitrary with respect to when anything was written, yet identical on every
 * render — a leader who reloads must not see the comments reshuffle, or
 * comparing two loads would start to reveal what a single ordering hides.
 */
const stableShuffle = (comments) => [...comments].sort((a, b) => a.id.localeCompare(b.id));
const clean = (text) => text.trim();
export const discloseComments = (comments) => {
    const written = comments.filter((comment) => clean(comment.text).length > 0);
    const supervisor = written.find((comment) => comment.relationship === 'SUPERVISOR');
    const self = written.find((comment) => comment.relationship === 'SELF');
    const anonymous = written.filter((comment) => comment.relationship !== 'SUPERVISOR' && comment.relationship !== 'SELF');
    const enough = anonymous.length >= MIN_POOLED_COMMENTS;
    return {
        supervisor: supervisor ? clean(supervisor.text) : null,
        self: self ? clean(self.text) : null,
        pooled: enough ? stableShuffle(anonymous).map((comment) => clean(comment.text)) : [],
        withheld: enough ? 0 : anonymous.length,
    };
};
/**
 * Whether this rater's words will carry their name.
 *
 * The survey tells them before they write. Somebody who would phrase a
 * criticism differently knowing it is attributed deserves to know which one
 * they are doing.
 */
export const commentIsAttributed = (relationship) => relationship === 'SUPERVISOR';
/**
 * Who is reading the report, for the purpose of what prose they may see.
 *
 * Three audiences, three different answers, and the difference between them is
 * the whole confidentiality story of this section:
 *
 *  - REVIEWER — the assigned coach, or an admin. Sees the synthesis at any
 *    stage and the sentences it was built from, because a synthesis nobody can
 *    check against its source is not reviewable.
 *  - SUBJECT — the leader the report is about. Sees the synthesis once a coach
 *    has approved it, plus their supervisor's attributed comment and their own
 *    words. Never the anonymous sentences.
 *  - HR — P&O. Sees the approved synthesis and nothing else written by anyone.
 *    Not the anonymous pool, not the supervisor's comment, not the leader's own
 *    words. Their job is the cycle, not what one colleague said about another,
 *    and an internal HR team is the reader every rater was most worried about
 *    when they were promised anonymity.
 */
export const REPORT_AUDIENCES = ['REVIEWER', 'SUBJECT', 'HR'];
/**
 * Cut the disclosed feedback down to what this reader may have.
 *
 * Returns the payload rather than flags for the interface to respect: a field
 * that is empty here is empty in the response, so there is nothing for a
 * curious reader to find in the network tab.
 */
export const feedbackForAudience = (audience, disclosed, summary) => {
    const writtenCount = disclosed.pooled.length + disclosed.withheld;
    const approved = summary?.status === 'APPROVED' ? summary : null;
    if (audience === 'REVIEWER') {
        return {
            supervisor: disclosed.supervisor,
            self: disclosed.self,
            summary,
            verbatimForReview: disclosed.pooled,
            withheld: disclosed.withheld,
            writtenCount,
        };
    }
    if (audience === 'SUBJECT') {
        return {
            supervisor: disclosed.supervisor,
            self: disclosed.self,
            summary: approved,
            verbatimForReview: [],
            withheld: disclosed.withheld,
            writtenCount,
        };
    }
    return {
        supervisor: null,
        self: null,
        summary: approved,
        verbatimForReview: [],
        // How many people wrote is itself a number about a small group, and P&O has
        // no use for it. They get the synthesis or they get nothing.
        withheld: 0,
        writtenCount: 0,
    };
};
//# sourceMappingURL=comments.js.map